import * as React from 'react';
import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { useQuery } from '@apollo/client';
import type { ApolloError } from 'apollo-client';

import {
  getDegreeLearnerCourseStates,
  mapNewInProgressCourses,
  mapNewNotStartedCourses,
  mapNewPassedCourses,
} from './UpcomingCourses/UpcomingCoursesQueries';
import type {
  NormalizedInProgressCourse,
  NormalizedNotStartedCourse,
  NormalizedPassedCourse,
} from './types/courseStates';

type NewCourseBase = {
  course: { id: string; name: string; slug: string };
};

type NewNotStartedCourseState = NewCourseBase & {
  activeSessionId: string | null;
  hasEnrollableSession: boolean;
};

type NewInProgressCourseState = NewCourseBase & {
  activeSessionId: string;
  hasEnrollableSession: boolean;
};

type GetDegreeLearnerCourseStatesQueryData = {
  DegreeHome: {
    degreeLearnerCourseStatesByUserIdAndDegreeId: {
      id: string;
      notStarted: NewNotStartedCourseState[];
      passed: NewCourseBase[];
      inProgress: NewInProgressCourseState[];
    } | null;
  };
};

type GetDegreeLearnerCourseStatesQueryVariables = {
  degreeId: string;
};

type UpcomingCoursesFromGraphQLProps = {
  upcomingCoursesData?: NormalizedNotStartedCourse[];
  passedCoursesData?: NormalizedPassedCourse[];
  inProgressCoursesData?: NormalizedInProgressCourse[];
  coursesLoading: boolean;
  coursesError?: ApolloError;
  refetchCourses?: () => void;
};

type InputProps = {
  degreeId: string;
};

type ContextProps = UpcomingCoursesFromGraphQLProps & {
  showEnrollmentDialog: boolean;
  setShowEnrollmentDialog: Dispatch<SetStateAction<boolean>>;
};

export const CoursesContext = React.createContext<ContextProps>({
  coursesLoading: false,
  showEnrollmentDialog: false,
  setShowEnrollmentDialog: () => null,
});

const CoursesProvider = ({ children, degreeId }: React.PropsWithChildren<InputProps>) => {
  const [showEnrollmentDialog, setShowEnrollmentDialog] = useState(false);

  const { data, loading, error, refetch } = useQuery<
    GetDegreeLearnerCourseStatesQueryData,
    GetDegreeLearnerCourseStatesQueryVariables
  >(getDegreeLearnerCourseStates, {
    variables: { degreeId },
  });

  const result = data?.DegreeHome?.degreeLearnerCourseStatesByUserIdAndDegreeId;

  const upcomingCoursesData = result?.notStarted ? mapNewNotStartedCourses(result.notStarted) : undefined;
  const passedCoursesData = result?.passed ? mapNewPassedCourses(result.passed) : undefined;
  const inProgressCoursesData = result?.inProgress ? mapNewInProgressCourses(result.inProgress) : undefined;

  return (
    <CoursesContext.Provider
      value={{
        upcomingCoursesData,
        passedCoursesData,
        inProgressCoursesData,
        coursesLoading: loading,
        coursesError: error,
        refetchCourses: refetch,
        showEnrollmentDialog,
        setShowEnrollmentDialog,
      }}
    >
      {children}
    </CoursesContext.Provider>
  );
};

export const useCoursesContext = () => React.useContext(CoursesContext);

export default CoursesProvider;
